/**
 * Programmatic zoom/pan controller for an infinite-canvas preview.
 *
 * Wraps a host element (the rendered RPUI content) inside a viewport/canvas
 * structure and exposes imperative methods for zoom, pan, and focus.
 * No UI controls are rendered — callers drive everything programmatically.
 */

export interface CanvasControllerOpts {
  /** Fallback margin (px) from top-left when resetting. Default: 48 */
  fitPadding?: number;
  /** Left margin (px) when resetting with hAlign "left". Defaults to fitPadding. */
  leftPadding?: number;
  /** Top margin (px) when resetting with vAlign "top". Defaults to fitPadding. */
  topPadding?: number;
  /** Horizontal alignment for reset. Default: "left" */
  hAlign?: "left" | "center";
  /** Vertical alignment for reset. Default: "top" */
  vAlign?: "top" | "center";
  /** @deprecated No longer used (fit was removed). Kept for compatibility. */
  maxFitScale?: number;
  /** @deprecated No longer used (fit was removed). Kept for compatibility. */
  fitSelector?: string | null;
  /** Minimum zoom scale. Default: 0.1 */
  minZoom?: number;
  /** Maximum zoom scale. Default: 4 */
  maxZoom?: number;
}

export class CanvasController {
  private canvas: HTMLElement;
  private viewport: HTMLElement;
  private host: HTMLElement;
  private scale = 1;
  private _panX = 0;
  private _panY = 0;
  private opts: Required<CanvasControllerOpts>;
  private ro: ResizeObserver | null = null;
  private wheelHandler: ((e: WheelEvent) => void) | null = null;
  private pointerDownHandler: ((e: PointerEvent) => void) | null = null;
  private pointerMoveHandler: ((e: PointerEvent) => void) | null = null;
  private pointerUpHandler: ((e: PointerEvent) => void) | null = null;
  private panning = false;
  private panStartX = 0;
  private panStartY = 0;
  private panOrigX = 0;
  private panOrigY = 0;
  private panEnabled = true;
  private spacePressed = false;
  private keyDownHandler: ((e: KeyboardEvent) => void) | null = null;
  private keyUpHandler: ((e: KeyboardEvent) => void) | null = null;
  private pinClickHandler: ((e: PointerEvent) => void) | null = null;
  private clickHandler: ((e: MouseEvent) => void) | null = null;
  private suppressClick = false;
  private dragged = false;
  private static DRAG_THRESHOLD = 5;

  /**
   * @param host The element containing rendered RPUI content (e.g. a div with <page-el>)
   * @param opts Configuration options
   */
  constructor(host: HTMLElement, opts: CanvasControllerOpts = {}) {
    this.host = host;
    this.opts = {
      fitPadding: opts.fitPadding ?? 48,
      leftPadding: opts.leftPadding ?? (opts.fitPadding ?? 48),
      topPadding: opts.topPadding ?? (opts.fitPadding ?? 48),
      hAlign: opts.hAlign ?? "left",
      vAlign: opts.vAlign ?? "top",
      maxFitScale: opts.maxFitScale ?? 2,
      minZoom: opts.minZoom ?? 0.1,
      maxZoom: opts.maxZoom ?? 4,
      fitSelector: opts.fitSelector ?? null,
    };

    // Build canvas > viewport > host structure.
    // Both carry `rpui-scope` so the scoped dark-mode token selector
    // `.rpui-scope[data-rp-theme="dark"]` matches them — the canvas/viewport
    // are PARENTS of the host, so they cannot inherit the host's scoped
    // CSS variables and need their own attribute + class.
    this.canvas = document.createElement("div");
    this.canvas.className = "rpui-scope";
    this.canvas.style.cssText =
      "flex:1;min-height:0;position:relative;overflow:hidden;cursor:grab;user-select:none;background:var(--rp-bg);color:var(--rp-text);border-radius:0;";

    this.viewport = document.createElement("div");
    this.viewport.className = "rpui-scope";
    this.viewport.style.cssText =
      "position:absolute;top:50%;left:50%;transform-origin:center center;background:var(--rp-bg);color:var(--rp-text);border-radius:0;";

    // Move host's parent to canvas, viewport wraps host
    const parent = host.parentElement;
    if (parent) {
      parent.insertBefore(this.canvas, host);
      parent.removeChild(host);
    }
    this.viewport.appendChild(host);
    this.canvas.appendChild(this.viewport);

    // Mirror the host's dark-mode attribute so the canvas background adapts.
    const initialTheme = host.dataset.rpTheme || "light";
    this.canvas.dataset.rpTheme = initialTheme;
    this.viewport.dataset.rpTheme = initialTheme;

    this.applyTransform();
    this.attachListeners();
  }

  private applyTransform() {
    this.viewport.style.transform = `translate(-50%, -50%) translate(${this._panX}px, ${this._panY}px) scale(${this.scale})`;
  }

  /** Current zoom scale (0.1 – 4). */
  get zoom(): number {
    return this.scale;
  }

  /** Current pan X offset in screen pixels. */
  get panX(): number {
    return this._panX;
  }

  /** Current pan Y offset in screen pixels. */
  get panY(): number {
    return this._panY;
  }

  /** Enable/disable drag-to-pan.
   *  When disabled (pick mode), dragging the canvas background still pans —
   *  only clicks on content elements are excluded so pick selection works. */
  setPanEnabled(enabled: boolean) {
    this.panEnabled = enabled;
    this.canvas.style.cursor = enabled ? "grab" : "crosshair";
  }

  /** Update dark mode on the canvas/viewport wrappers so the background
   *  follows the host. Both need the attribute because they are parents of
   *  the host and carry `rpui-scope` to match the scoped dark token selector. */
  setDark(dark: boolean) {
    const val = dark ? "dark" : "light";
    this.canvas.dataset.rpTheme = val;
    this.viewport.dataset.rpTheme = val;
  }

  /** The canvas DOM element (for attaching overlay UI if needed). */
  get canvasEl(): HTMLElement {
    return this.canvas;
  }

  /** The viewport DOM element. */
  get viewportEl(): HTMLElement {
    return this.viewport;
  }

  private attachListeners() {
    this.wheelHandler = (e: WheelEvent) => {
      e.preventDefault();
      if (e.ctrlKey || e.metaKey) {
        const delta = -e.deltaY * 0.01;
        this.setZoom(this.scale * (1 + delta), e.clientX, e.clientY);
      } else {
        this._panX -= e.deltaX;
        this._panY -= e.deltaY;
        this.applyTransform();
      }
    };
    this.canvas.addEventListener("wheel", this.wheelHandler, {
      passive: false,
    });

    this.pointerDownHandler = (e: PointerEvent) => {
      // Cmd/Ctrl+click on pin: don't start pan, let pin handler work
      if (
        (e.metaKey || e.ctrlKey) &&
        e.target instanceof Element &&
        e.target.closest(".rp-pin")
      )
        return;
      this.panning = true;
      this.dragged = false;
      this.suppressClick = false;
      this.panStartX = e.clientX;
      this.panStartY = e.clientY;
      this.panOrigX = this._panX;
      this.panOrigY = this._panY;
      this.canvas.classList.add("panning");
      this.canvas.style.cursor = "grabbing";
      // In pick mode, skip pointer capture so click events still reach
      // content elements for selection. In view mode, capture for smooth
      // dragging even when the pointer leaves the canvas bounds.
      if (this.panEnabled || this.spacePressed) {
        this.canvas.setPointerCapture(e.pointerId);
      }
    };
    this.pointerMoveHandler = (e: PointerEvent) => {
      if (!this.panning) return;
      const dx = e.clientX - this.panStartX;
      const dy = e.clientY - this.panStartY;
      if (
        !this.dragged &&
        dx * dx + dy * dy >
          CanvasController.DRAG_THRESHOLD * CanvasController.DRAG_THRESHOLD
      )
        this.dragged = true;
      this._panX = this.panOrigX + dx;
      this._panY = this.panOrigY + dy;
      this.applyTransform();
    };
    this.pointerUpHandler = (e: PointerEvent) => {
      this.panning = false;
      this.canvas.classList.remove("panning");
      this.canvas.style.cursor = this.panEnabled ? "grab" : "crosshair";
      try {
        this.canvas.releasePointerCapture(e.pointerId);
      } catch {}
      // If a drag happened in pick mode, suppress the click that follows
      // so pick selection doesn't fire after a pan gesture.
      this.suppressClick = this.dragged && !this.panEnabled;
    };
    this.canvas.addEventListener("pointerdown", this.pointerDownHandler);
    this.canvas.addEventListener("pointermove", this.pointerMoveHandler);
    this.canvas.addEventListener("pointerup", this.pointerUpHandler);

    // In pick mode, suppress the click that follows a drag so it doesn't
    // toggle selection. Listen in document capture phase before the host's
    // pick handler gets a chance.
    this.clickHandler = (e: MouseEvent) => {
      if (!this.suppressClick) return;
      if (!this.canvas.contains(e.target as Node)) return;
      e.stopPropagation();
      this.suppressClick = false;
    };
    document.addEventListener("click", this.clickHandler, true);

    // Space+drag pan support
    this.keyDownHandler = (e: KeyboardEvent) => {
      if (e.code === "Space" && !this.spacePressed) {
        const tag = (e.target as HTMLElement)?.tagName;
        if (tag === "INPUT" || tag === "TEXTAREA") return;
        this.spacePressed = true;
        this.canvas.style.cursor = "grab";
        e.preventDefault();
      }
    };
    this.keyUpHandler = (e: KeyboardEvent) => {
      if (e.code === "Space") {
        this.spacePressed = false;
        this.canvas.style.cursor = this.panEnabled ? "grab" : "crosshair";
      }
    };
    window.addEventListener("keydown", this.keyDownHandler);
    window.addEventListener("keyup", this.keyUpHandler);

    // Cmd/Ctrl+click on pins: pan to and highlight the corresponding annotation
    this.pinClickHandler = (e: PointerEvent) => {
      if (!(e.metaKey || e.ctrlKey)) return;
      const pin =
        e.target instanceof Element ? e.target.closest(".rp-pin") : null;
      if (!pin) return;
      e.preventDefault();
      e.stopPropagation();
      const sectionId = pin.textContent?.trim();
      if (!sectionId) return;
      const target = this.host.querySelector(
        `[data-rp-section="${CSS.escape(sectionId)}"]`,
      );
      if (target) this.focusAnnotation(target);
    };
    this.host.addEventListener("pointerdown", this.pinClickHandler, true);
  }

  /** Set zoom scale, optionally anchored to a screen point. */
  setZoom(s: number, cx?: number, cy?: number) {
    const prev = this.scale;
    const next = Math.max(this.opts.minZoom, Math.min(this.opts.maxZoom, s));
    if (cx !== undefined && cy !== undefined) {
      const rect = this.canvas.getBoundingClientRect();
      const px = cx - rect.left - rect.width / 2;
      const py = cy - rect.top - rect.height / 2;
      this._panX = px - (px - this._panX) * (next / prev);
      this._panY = py - (py - this._panY) * (next / prev);
    }
    this.scale = next;
    this.applyTransform();
  }

  /** Zoom in by a factor (default 1.2). */
  zoomIn(factor = 1.2) {
    this.setZoom(this.scale * factor);
  }

  /** Zoom out by a factor (default 1.2). */
  zoomOut(factor = 1.2) {
    this.setZoom(this.scale / factor);
  }

  /** Reset zoom and pan to the configured alignment with a margin. */
  reset() {
    this.scale = 1;
    this._panX = 0;
    this._panY = 0;
    this.applyTransform();

    requestAnimationFrame(() => {
      const canvasRect = this.canvas.getBoundingClientRect();
      const hostRect = this.host.getBoundingClientRect();
      const { hAlign, vAlign, leftPadding, topPadding } = this.opts;
      this._panX =
        hAlign === "center"
          ? 0
          : leftPadding + hostRect.width / 2 - canvasRect.width / 2;
      this._panY =
        vAlign === "center"
          ? 0
          : topPadding + hostRect.height / 2 - canvasRect.height / 2;
      this.applyTransform();
    });
  }

  /** Pan the canvas to center a specific element in the view. */
  panToElement(el: Element) {
    const canvasRect = this.canvas.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    const elCx = elRect.left + elRect.width / 2;
    const elCy = elRect.top + elRect.height / 2;
    const canvasCx = canvasRect.left + canvasRect.width / 2;
    const canvasCy = canvasRect.top + canvasRect.height / 2;
    this._panX += canvasCx - elCx;
    this._panY += canvasCy - elCy;
    this.applyTransform();
  }

  /** Highlight an annotation block and pan to its title. */
  focusAnnotation(el: Element) {
    this.host
      .querySelectorAll(".rp-section-focus")
      .forEach((n) => n.classList.remove("rp-section-focus"));
    (el as HTMLElement).classList.add("rp-section-focus");
    setTimeout(
      () => (el as HTMLElement).classList.remove("rp-section-focus"),
      3000,
    );
    const head = el.querySelector(".annotation-el-head") || el;
    this.panToElement(head);
  }

  /** Destroy the controller, remove listeners and restore DOM. */
  destroy() {
    if (this.wheelHandler)
      this.canvas.removeEventListener("wheel", this.wheelHandler);
    if (this.pointerDownHandler)
      this.canvas.removeEventListener("pointerdown", this.pointerDownHandler);
    if (this.pointerMoveHandler)
      this.canvas.removeEventListener("pointermove", this.pointerMoveHandler);
    if (this.pointerUpHandler)
      this.canvas.removeEventListener("pointerup", this.pointerUpHandler);
    if (this.keyDownHandler)
      window.removeEventListener("keydown", this.keyDownHandler);
    if (this.keyUpHandler)
      window.removeEventListener("keyup", this.keyUpHandler);
    if (this.pinClickHandler)
      this.host.removeEventListener("pointerdown", this.pinClickHandler, true);
    if (this.clickHandler)
      document.removeEventListener("click", this.clickHandler, true);
    this.ro?.disconnect();

    // Restore host to original parent
    const parent = this.canvas.parentElement;
    if (parent) {
      parent.insertBefore(this.host, this.canvas);
      parent.removeChild(this.canvas);
    }
  }
}
